function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { selx } from 'clsx';
import { jsx } from 'panel';
import { Translatable } from 'common/i18n';
import { ButtonEl } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { isArgumentListError, isBadRequestError, isCardinalityLimitError, isCohortDateRangeError, isDataClassificationError, isHPCListNotSupported, isInvalidFormulasError, isMissingDataGroupError, isMultipleHPCWithList, isTimeoutError, isUnauthorizedWorkspaceError, isUnsupportedAttributionError, isUserVisibleNotFoundError, isUserVisibleParamsError, isUserVisiblePreconditionFailedError, trackMixpanelTips, trimUserVisibleNotFoundError, trimUserVisibleParamsError, trimUserVisiblePreconditionFailedError } from 'common/report/queries/utils';
import { getMissingDatagroupMappedProperty, isMissingDatagroupMappedPropertyLookup } from 'common/report/util/properties';
import { SpinnerEl } from 'common/components/spinner';
import { SpinnerSize, SpinnerTheme } from 'common/components/spinner/types';
import { BaseQueryErrorType } from 'common/report/types';
import { WarningTheme } from 'common/components/warning/constants';
import { spans } from 'common/util/tracing';
import { QuerySamplingCtaEl } from './query-sampling-cta';
import { EMPTY_REPORT_HEADER, NoDataReason, ReportStatus, TIPS, WarningHeroReportName, WarningHeroTheme, WarningTrackingError } from './constants';
import { WarningHeroEl } from './index';
import styles from './presets.module.styl';
import { NoDataHeroPanel } from './no-data-hero';
export class TipFactory {
  static getTip() {
    if (TipFactory.index === -1) {
      TipFactory.index = Math.floor(Math.random() * TIPS.length);
    }
    return TIPS[TipFactory.index];
  }
  static resetTip() {
    TipFactory.index = -1;
    TipFactory.startEpoch = 0;
  }
}
_defineProperty(TipFactory, "index", -1);
_defineProperty(TipFactory, "startEpoch", 0);
export function TipsHero() {
  const tip = TipFactory.getTip();
  return <WarningHeroEl attrs={{
    theme: WarningTheme.Tip
  }} params={{
    headerText: tip.title || "",
    bodyText: tip.body || ""
  }} hook={{
    insert: () => {
      trackMixpanelTips(TipFactory.index);
      TipFactory.startEpoch = Date.now();
    },
    destroy: () => {
      trackMixpanelTips(TipFactory.index, Date.now() - TipFactory.startEpoch);
      TipFactory.resetTip();
    }
  }} data-sentry-element="WarningHeroEl" data-sentry-component="TipsHero" data-sentry-source-file="presets.tsx" />;
}
export function LoadingHero() {
  let params: {
    bodyText?: string;
    headerText?: string;
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    headerText,
    bodyText
  } = params || {};
  return <WarningHeroEl attrs={{
    elref: "loadingHero",
    theme: WarningHeroTheme.Loading
  }} params={{
    headerText: headerText || "",
    bodyText: bodyText || ""
  }} data-sentry-element="WarningHeroEl" data-sentry-component="LoadingHero" data-sentry-source-file="presets.tsx" />;
}
export function SensitiveDataHero(_ref: {
  bodyText?: string;
  reportName: WarningHeroReportName;
}) {
  let {
    bodyText,
    reportName
  }: {
    bodyText?: string;
    reportName: WarningHeroReportName;
  } = _ref;
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.Sensitive,
    report: reportName
  }} params={{
    headerText: _ref2 => {
      let {
        t
      } = _ref2;
      return t("warning-hero.sensitive-data");
    },
    bodyText: bodyText || ""
  }} data-sentry-element="WarningHeroEl" data-sentry-component="SensitiveDataHero" data-sentry-source-file="presets.tsx" />;
}
export function UnsupportedActionHero(_ref3: {
  bodyText: Translatable;
  reportName: WarningHeroReportName;
}) {
  let {
    bodyText,
    reportName
  }: {
    bodyText: Translatable;
    reportName: WarningHeroReportName;
  } = _ref3;
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.Error,
    report: reportName,
    'tracking-error': WarningTrackingError.UnsupportedActionError
  }} params={{
    headerText: _ref4 => {
      let {
        t
      } = _ref4;
      return t("warning-hero.unsupported");
    },
    bodyText
  }} data-sentry-element="WarningHeroEl" data-sentry-component="UnsupportedActionHero" data-sentry-source-file="presets.tsx" />;
}
export function UnauthorizedWorkspaceError(_ref5: {
  reportName: WarningHeroReportName;
}) {
  let {
    reportName
  }: {
    reportName: WarningHeroReportName;
  } = _ref5;
  // Most commonly raised for dashboard digests, as the page normally redirects to a valid workspace
  // (see `findCurrentValidWorkspaceId` in chrome-header)
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.Sensitive,
    report: reportName
  }} params={{
    headerText: _ref6 => {
      let {
        t
      } = _ref6;
      return t("warning-hero.unauthorized-workspace") || "";
    },
    bodyText: ""
  }} data-sentry-element="WarningHeroEl" data-sentry-component="UnauthorizedWorkspaceError" data-sentry-source-file="presets.tsx" />;
}
export function MissingDataGroupMappingHero(_ref7: {
  property: string;
  reportName: WarningHeroReportName;
  isLookupProperty: boolean;
}) {
  let {
    property,
    reportName,
    isLookupProperty
  }: {
    property: string;
    reportName: WarningHeroReportName;
    isLookupProperty: boolean;
  } = _ref7;
  if (isLookupProperty) {
    return <WarningHeroEl attrs={{
      theme: WarningHeroTheme.Error,
      report: reportName,
      'tracking-error': WarningTrackingError.MissingMappedDatagroup
    }} params={{
      headerText: _ref8 => {
        let {
          t
        } = _ref8;
        return t("warning-hero.unsupported");
      },
      bodyText: _ref9 => {
        let {
          t
        } = _ref9;
        return t("warning-hero.lookup-property-unavailable", {
          property
        });
      }
    }} />;
  }
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.Error,
    report: reportName,
    'tracking-error': WarningTrackingError.MissingMappedDatagroup
  }} params={{
    headerText: _ref10 => {
      let {
        t
      } = _ref10;
      return t("warning-hero.unsupported");
    },
    bodyText: _ref11 => {
      let {
        t
      } = _ref11;
      return t("warning-hero.property-unavailable", {
        property
      });
    }
  }} data-sentry-element="WarningHeroEl" data-sentry-component="MissingDataGroupMappingHero" data-sentry-source-file="presets.tsx" />;
}
export function ServerErrorHero(_ref12: {
  headerText?: Translatable;
  bodyText?: Translatable;
  reportName: WarningHeroReportName;
}) {
  let {
    headerText,
    bodyText,
    reportName
  }: {
    headerText?: Translatable;
    bodyText?: Translatable;
    reportName: WarningHeroReportName;
  } = _ref12;
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.Error,
    report: reportName
  }} params={{
    headerText: headerText || (_ref13 => {
      let {
        t
      } = _ref13;
      return t("warning-hero.server-error");
    }),
    bodyText: bodyText || ""
  }} data-sentry-element="WarningHeroEl" data-sentry-component="ServerErrorHero" data-sentry-source-file="presets.tsx" />;
}
export function EmptyDashboardAddReportHeroTextCards() {
  let _props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  let childrenActions = arguments.length > 1 ? arguments[1] : undefined;
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.EmptyReport,
    report: WarningHeroReportName.Dashboards
  }} params={{
    headerText: "Add a new chart, text or use a template to get started."
  }} data-sentry-element="WarningHeroEl" data-sentry-component="EmptyDashboardAddReportHeroTextCards" data-sentry-source-file="presets.tsx">
      {childrenActions}
    </WarningHeroEl>;
}
export function EmptyLookupTableHero(_ref14) {
  let {
    onImportLookupTableClick
  } = _ref14;
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.EmptyReport,
    report: WarningHeroReportName.LookupTables
  }} params={{
    headerText: EMPTY_REPORT_HEADER[WarningHeroReportName.LookupTables]
  }} data-sentry-element="WarningHeroEl" data-sentry-component="EmptyLookupTableHero" data-sentry-source-file="presets.tsx">
      <ButtonEl attrs={{
      icon: "plus",
      theme: ButtonTheme.Primary
    }} on={{
      click: onImportLookupTableClick
    }} data-sentry-element="ButtonEl" data-sentry-source-file="presets.tsx">
        Import Lookup Table
      </ButtonEl>
    </WarningHeroEl>;
}
export function InitialReportHero(_ref15: {
  reportName: WarningHeroReportName;
}) {
  let {
    reportName
  }: {
    reportName: WarningHeroReportName;
  } = _ref15;
  const theme = WarningHeroTheme.EmptyReport;
  return <WarningHeroEl attrs={{
    theme,
    report: reportName
  }} params={{
    headerText: EMPTY_REPORT_HEADER[reportName]
  }} data-sentry-element="WarningHeroEl" data-sentry-component="InitialReportHero" data-sentry-source-file="presets.tsx" />;
}
export function EmptyReportHero(_ref16: {
  reportName: WarningHeroReportName;
}) {
  let {
    reportName
  }: {
    reportName: WarningHeroReportName;
  } = _ref16;
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.EmptyReport,
    report: reportName
  }} params={{
    headerText: EMPTY_REPORT_HEADER[reportName]
  }} data-sentry-element="WarningHeroEl" data-sentry-component="EmptyReportHero" data-sentry-source-file="presets.tsx" />;
}
export function InvalidFormulasHero(_ref17: {
  reportName: WarningHeroReportName;
}) {
  let {
    reportName
  }: {
    reportName: WarningHeroReportName;
  } = _ref17;
  return <WarningHeroEl attrs={{
    theme: WarningHeroTheme.Error,
    report: reportName
  }} params={{
    headerText: _ref18 => {
      let {
        t
      } = _ref18;
      return t("warning-hero.invalid-formulas");
    }
  }} data-sentry-element="WarningHeroEl" data-sentry-component="InvalidFormulasHero" data-sentry-source-file="presets.tsx" />;
}
const CONTACT_SUPPORT_MESSAGE = _ref19 => {
  let {
    t
  } = _ref19;
  return t("warning-hero.contact-support");
};
export function ReportStatusHero(_ref20: {
  reportName: WarningHeroReportName;
  queryError?: BaseQueryErrorType;
  reportStatus?: ReportStatus;
  showQuerySamplingCTA?: boolean;
  showMpTips?: boolean;
  enableQuerySampling?: (ev) => void;
  noDataReason?: NoDataReason;
}) {
  let {
    reportName,
    queryError,
    reportStatus,
    showQuerySamplingCTA,
    showMpTips,
    enableQuerySampling,
    noDataReason
  }: {
    reportName: WarningHeroReportName;
    queryError?: BaseQueryErrorType;
    reportStatus?: ReportStatus;
    showQuerySamplingCTA?: boolean;
    showMpTips?: boolean;
    enableQuerySampling?: (ev) => void;
    noDataReason?: NoDataReason;
  } = _ref20;
  if (queryError) {
    const {
      error,
      status,
      data
    } = queryError;
    if (isDataClassificationError(error) || status === 423) {
      return <SensitiveDataHero bodyText={error} reportName={reportName} />;
    } else if (isUnauthorizedWorkspaceError({
      status,
      error
    })) {
      return <UnauthorizedWorkspaceError reportName={reportName} />;
    } else if (isMissingDataGroupError(error)) {
      const property = getMissingDatagroupMappedProperty(data);
      const isLookupProperty = isMissingDatagroupMappedPropertyLookup(data);
      return <MissingDataGroupMappingHero property={property} reportName={reportName} isLookupProperty={isLookupProperty} />;
    } else if (isCardinalityLimitError(error)) {
      return <UnsupportedActionHero bodyText={_ref21 => {
        let {
          t
        } = _ref21;
        return t("warning-hero.cardinality-limit");
      }} reportName={reportName} />;
    } else if (isArgumentListError(error)) {
      return <UnsupportedActionHero bodyText={_ref22 => {
        let {
          t
        } = _ref22;
        return t("warning-hero.too-many-arguments");
      }} reportName={reportName} />;
    } else if (isInvalidFormulasError(error)) {
      return <InvalidFormulasHero reportName={reportName} />;
    } else if (isUnsupportedAttributionError(error)) {
      return <UnsupportedActionHero bodyText={error} reportName={reportName} />;
    } else if (isCohortDateRangeError(error)) {
      return <UnsupportedActionHero bodyText={error} reportName={reportName} />;
    } else if (isHPCListNotSupported(error)) {
      return <UnsupportedActionHero bodyText={_ref23 => {
        let {
          t
        } = _ref23;
        return t("warning-hero.list-property-constant");
      }} reportName={reportName} />;
    } else if (isMultipleHPCWithList(error)) {
      return <UnsupportedActionHero bodyText={_ref24 => {
        let {
          t
        } = _ref24;
        return t("warning-hero.hpc-limit-with-list");
      }} reportName={reportName} />;
    } else if (isUserVisibleParamsError(error)) {
      return <UnsupportedActionHero bodyText={trimUserVisibleParamsError(error)} reportName={reportName} />;
    } else if (isUserVisiblePreconditionFailedError(error)) {
      return <UnsupportedActionHero bodyText={trimUserVisiblePreconditionFailedError(error)} reportName={reportName} />;
    } else if (isUserVisibleNotFoundError(error)) {
      return <UnsupportedActionHero bodyText={trimUserVisibleNotFoundError(error)} reportName={reportName} />;
    } else if (status === 412) {
      return <UnsupportedActionHero bodyText={_ref25 => {
        let {
          t
        } = _ref25;
        return t("warning-hero.precondition-failed");
      }} reportName={reportName} />;
    } else if (isBadRequestError(status) || status === 422) {
      return <ServerErrorHero headerText={_ref26 => {
        let {
          t
        } = _ref26;
        return t("warning-hero.bad-request");
      }} bodyText={CONTACT_SUPPORT_MESSAGE} reportName={reportName} />;
    } else if (isTimeoutError(status)) {
      return <ServerErrorHero headerText={_ref27 => {
        let {
          t
        } = _ref27;
        return t("warning-hero.timeout");
      }} bodyText={_ref28 => {
        let {
          t,
          print
        } = _ref28;
        return t("warning-hero.timeout-remedy") + "\n" + print(CONTACT_SUPPORT_MESSAGE);
      }} reportName={reportName} />;
    }
    return <ServerErrorHero bodyText={CONTACT_SUPPORT_MESSAGE} reportName={reportName} />;
  }
  switch (reportStatus) {
    case ReportStatus.Error:
      // Fallback case for generic report errors that might be raised by the UX pre-query, such as
      // invalid bookmarks.
      return <ServerErrorHero bodyText={CONTACT_SUPPORT_MESSAGE} reportName={reportName} />;
    case ReportStatus.Initial:
      return <InitialReportHero reportName={reportName} />;
    case ReportStatus.Empty:
      return <EmptyReportHero reportName={reportName} />;
    case ReportStatus.NoData:
      return <NoDataHeroPanel bodyText={_ref29 => {
        let {
          t
        } = _ref29;
        return t(noDataReason === NoDataReason.EmptyProject ? "insights.no-data.remedy-empty-project" : "insights.no-data.remedy");
      }} reportName={reportName} reason={noDataReason} />;
    case ReportStatus.Loading:
      return <div hook={{
        insert: () => spans.getOrCreateSpan({
          id: "report.loading",
          parentId: "report-query"
        }),
        destroy: () => spans.endSpanById("report.loading")
      }} sel={selx(styles.loadingState)}>
          {/* IMPORTANT: do not change .chart-loader selector unless modifying screenshot_service/report_screenshot.js first */}
          <SpinnerEl sel='.chart-loader' attrs={{
          size: SpinnerSize.Medium,
          elref: "chartLoader",
          theme: SpinnerTheme.Fancy
        }} />
          <div sel='tip-container' style={{
          height: "120px"
        }}>
            {showQuerySamplingCTA ? <QuerySamplingCtaEl attrs={{
            slot: "loading"
          }} on={{
            enable: enableQuerySampling
          }} /> : showMpTips ? <TipsHero /> : null}
          </div>
        </div>;
    case ReportStatus.Success:
      // Success should not show any warning hero, return null to render the report normally
      return null;
    default:
      return null;
  }
}